import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./my-windfarms.css";
import { userAPI, windfarmAPI } from "../../api";

const VIEWS = { all: "All wind farms", mine: "Selected" };

function errorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  return fallback;
}

// Staff curate which wind farms they work with. The Home page lists exactly
// these. Each switch saves on its own, so there is no Save button to forget.
export default function MyWindfarmsPage() {
  const [windfarms, setWindfarms] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [busyIds, setBusyIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all");

  useEffect(() => {
    Promise.all([windfarmAPI.listAllWindfarms(), userAPI.listMyWindfarms()])
      .then(([all, mine]) => {
        setWindfarms([...all].sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedIds(new Set(mine.map((w) => w.id)));
      })
      .catch((err) => setLoadError(errorMessage(err, "Could not load your wind farms. Reload the page to try again.")))
      .finally(() => setLoading(false));
  }, []);

  const toggle = useCallback(
    (wf) => {
      if (busyIds.has(wf.id)) return;
      const wasOn = selectedIds.has(wf.id);
      const withBusy = (set) => new Set(set).add(wf.id);
      const withoutBusy = (set) => {
        const next = new Set(set);
        next.delete(wf.id);
        return next;
      };

      // Switch immediately, undo if the server refuses.
      setSaveError("");
      setBusyIds(withBusy);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (wasOn) next.delete(wf.id);
        else next.add(wf.id);
        return next;
      });

      const request = wasOn ? userAPI.removeWindfarmFromMe(wf.id) : userAPI.addWindfarmToMe(wf.id);
      request
        .then((assigned) => setSelectedIds(new Set(assigned.map((w) => w.id))))
        .catch((err) => {
          setSelectedIds((prev) => {
            const next = new Set(prev);
            if (wasOn) next.add(wf.id);
            else next.delete(wf.id);
            return next;
          });
          setSaveError(errorMessage(err, `Could not update ${wf.name}. Try again.`));
        })
        .finally(() => setBusyIds(withoutBusy));
    },
    [busyIds, selectedIds],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return windfarms.filter((wf) => {
      if (view === "mine" && !selectedIds.has(wf.id)) return false;
      if (!q) return true;
      return wf.name.toLowerCase().includes(q) || (wf.customer || "").toLowerCase().includes(q);
    });
  }, [windfarms, selectedIds, search, view]);

  const emptyMessage = () => {
    if (search.trim()) return "No wind farms match your search.";
    if (view === "mine") return "You haven't selected any wind farms yet. Open All wind farms and switch on the ones you work with.";
    return "There are no wind farms to show.";
  };

  return (
    <div className="my-windfarms-page">
      <div className="my-windfarms-wrapper">
        <h1>My wind farms</h1>
        <p className="lede">Choose the wind farms you work with. Your Home page lists the ones you select. Changes save as you switch them.</p>

        {loadError && <p className="notice error" role="alert">{loadError}</p>}

        {!loadError && (
          <>
            <div className="toolbar">
              <input
                id="my-windfarms-search"
                className="search-input"
                type="search"
                placeholder="Search by wind farm or customer…"
                aria-label="Search wind farms"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="segmented" role="group" aria-label="Show">
                {Object.entries(VIEWS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={view === key ? "active" : ""}
                    aria-pressed={view === key}
                    onClick={() => setView(key)}
                  >
                    {label}
                    <span className="count">{key === "all" ? windfarms.length : selectedIds.size}</span>
                  </button>
                ))}
              </div>
            </div>

            <p className="notice error" role="alert" hidden={!saveError}>{saveError}</p>

            <div className="windfarm-rows" aria-busy={loading}>
              {loading && <p className="empty-state">Loading wind farms…</p>}
              {!loading && visible.length === 0 && <p className="empty-state">{emptyMessage()}</p>}
              {!loading &&
                visible.map((wf) => {
                  const on = selectedIds.has(wf.id);
                  return (
                    <button
                      key={wf.id}
                      type="button"
                      className={`windfarm-row ${on ? "on" : ""}`}
                      aria-pressed={on}
                      disabled={busyIds.has(wf.id)}
                      onClick={() => toggle(wf)}
                    >
                      <span className="info">
                        <strong>{wf.name}</strong>
                        <span className="customer">{wf.customer}</span>
                      </span>
                      <span className="switch" aria-hidden="true" />
                    </button>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
